import React, { useRef, useState } from 'react';

import WButton from 'shared_components/components/WForms/WButton/WButton';
import WModal from 'shared_components/components/WModal';

import {
  RecordingEditForm,
  type CreatedRecordingSummary,
  type RecordingEditFormHandle,
} from './RecordingEditForm';

export type { CreatedRecordingSummary };
import type { CreateRecordingResponse } from 'shared_components/generated/admin';

export interface CreateRecordingModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId?: string;
  availableFormats?: string[];
  availableTypes?: string[];
  availableContributions?: string[];
  availableArtists?: string[];
  /**
   * A recording the backend actually created — `created` only.
   *
   * The stub never reaches this, by design: `not_implemented` stored nothing,
   * so there is nothing for the page to go and fetch. The grid's post-create
   * refetch hangs off this callback.
   */
  onCreated?: (
    response: CreateRecordingResponse,
    summary: CreatedRecordingSummary
  ) => void;
}

/**
 * The shell for manual recording creation (WMC-515).
 *
 * Its own file rather than a sibling of `EditRecordingModal` inside
 * `MasterDiscography.tsx`: release selection, the unmet-requirements checklist
 * and response branching all land here, and that file is already ~4,900 lines.
 *
 * The shell holds no form state of its own. `RecordingEditForm` in
 * `mode="create"` seeds every field empty and seeds its baseline with those
 * same empties, so create is not a second code path through the form — it is
 * the case where the baseline is empty.
 */
export const CreateRecordingModal: React.FC<CreateRecordingModalProps> = ({
  isOpen,
  onClose,
  userId,
  availableFormats = [],
  availableTypes = [],
  availableContributions = [],
  availableArtists = [],
  onCreated,
}) => {
  const formRef = useRef<RecordingEditFormHandle>(null);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  /**
   * The outcome of the last submission, or `null` while composing.
   *
   * Only the two success-shaped statuses land here. `invalid` never does — the
   * form renders it against the fields and the operator carries on in the same
   * draft (`create-invalid-inline`).
   */
  const [outcome, setOutcome] = useState<{
    response: CreateRecordingResponse;
    summary: CreatedRecordingSummary;
  } | null>(null);
  /** Bumped by "Create another" to remount the form, which is how it comes
   *  back blank — same mechanism as unmounting on close. */
  const [formKey, setFormKey] = useState(0);
  /**
   * Which arm the form is on (WMC-557). `add` once the existence lookup has
   * found the source id under another artist: the same modal, with its copy
   * changed — "Add Recording", "Recording added" — because what is about to
   * happen is different and the title should say so. Reset with the form.
   */
  const [kind, setKind] = useState<'create' | 'add'>('create');
  const adding = kind === 'add';

  /**
   * The single exit, for every way out of the modal.
   *
   * `WModal` routes both its close button and a backdrop click through
   * `onClose`, and the footer's Cancel is pointed here too, so all three are
   * guarded by construction rather than by remembering to guard each one.
   *
   * Confirms only when there is something to lose. A prompt on an untouched
   * form is noise, and noise is what teaches people to dismiss the prompt that
   * matters.
   */
  const requestClose = () => {
    if (formRef.current?.isDirty()) {
      setConfirmDiscard(true);
      return;
    }
    onClose();
  };

  const discard = () => {
    setConfirmDiscard(false);
    onClose();
  };

  const handleCreated = (
    response: CreateRecordingResponse,
    summary: CreatedRecordingSummary
  ) => {
    setOutcome({ response, summary });
    // The page is told about `created` only. Branching on `status` rather than
    // on anything in `errors` is the rule the whole response contract rests on.
    if (response.status === 'created') onCreated?.(response, summary);
  };

  /** Back to a blank draft without leaving the modal. `created` only — after
   *  `not_implemented` there is nothing to create another *of*, and wiping the
   *  operator's only copy of the work would be the second loss in a row. */
  const createAnother = () => {
    setOutcome(null);
    setKind('create');
    setFormKey((k) => k + 1);
  };

  // Unmounting rather than hiding: the form seeds its empty baseline off a
  // `[row, mode]` effect, so a closed-then-reopened modal must remount to come
  // back blank. Keeping it mounted would carry the previous attempt's typing
  // into the next one.
  if (!isOpen) return null;

  return (
    <WModal
      isOpen={isOpen}
      onClose={requestClose}
      title={adding ? 'Add Recording' : 'Create Recording'}
      modalClasses="tw-max-w-5xl tw-w-full tw-max-h-[90vh] tw-flex tw-flex-col tw-bg-gray-900 tw-overflow-hidden tw-mx-4"
    >
      {/* The explicit cap is what makes the form's own `flex-1` columns
          resolve. `WModal` renders its children inside a plain `<main>`, not a
          flex container, so this wrapper's `flex-1` has nothing to size
          against and it would otherwise grow to its content — pushing the
          footer past the modal's `max-h-[90vh]`, which clips it. 100px is the
          modal's chrome: 24px of padding top and bottom, the ~28px header and
          the 16px `mt-4` on `<main>`. */}
      <div className="tw-flex-1 tw-overflow-hidden tw-flex tw-flex-col tw-min-h-0 tw-max-h-[calc(90vh-100px)]">
        <RecordingEditForm
          key={formKey}
          ref={formRef}
          mode="create"
          // No recording exists yet, which is what puts the form on its
          // create branch.
          row={null}
          // Create commits once, from the form's own footer; there is no
          // per-section save to route anywhere.
          onSave={() => {}}
          onCreated={handleCreated}
          onModeChange={setKind}
          // Through the guard, not straight out: the footer's Cancel discards
          // exactly as much work as the close button does.
          onCancel={requestClose}
          userId={userId}
          availableFormats={availableFormats}
          availableTypes={availableTypes}
          availableContributions={availableContributions}
          availableArtists={availableArtists}
        />
      </div>

      {/* Outcome modals. Two of the three statuses reach here; `invalid` is a
          fixable state and renders inline in the form instead, because a modal
          over a fixable error is one more thing to dismiss before you can act
          on it. Neither modal promises a timeframe. */}
      <WModal
        isOpen={outcome?.response.status === 'not_implemented'}
        onClose={() => setOutcome(null)}
        title="Validated — not saved"
        modalClasses="tw-max-w-md tw-w-full tw-mx-4"
      >
        <div className="tw-mt-4">
          <p className="tw-text-white tw-text-sm tw-mb-6">
            &ldquo;{outcome?.summary.title}&rdquo; passed every check, but{' '}
            {adding ? 'adding an existing recording' : 'recording creation'}{' '}
            isn&rsquo;t live yet, so nothing has been stored. You&rsquo;ll need
            to re-enter this once it is switched on.
          </p>
          <div className="tw-flex tw-justify-end tw-gap-2">
            {/* Dismisses the outcome, not the draft. An operator who has just
                been told their work was not saved should not have it taken
                away by the same click — closing the create modal still goes
                through the discard guard. No "Create another": there is
                nothing to create another of. */}
            <WButton
              variant="primary"
              label="Close"
              onClick={() => setOutcome(null)}
            />
          </div>
        </div>
      </WModal>

      {/* The create is synchronous: by the time this shows, the recording is
          stored and the grid behind the modal is already refetching to show
          it. So the copy says what happened, not what will — the same thing a
          save, a bulk apply or a mass edit tell the operator. */}
      <WModal
        isOpen={outcome?.response.status === 'created'}
        onClose={() => setOutcome(null)}
        title={adding ? 'Recording added' : 'Recording created'}
        modalClasses="tw-max-w-md tw-w-full tw-mx-4"
      >
        <div className="tw-mt-4">
          <p className="tw-text-white tw-text-sm tw-mb-6">
            {adding ? (
              // Nothing was created: the Recording existed, and this artist
              // is now on it. The copy says that, not "created".
              <>
                &ldquo;{outcome?.summary.title}&rdquo; has been added to this
                artist&rsquo;s discography. The discography is refreshing to
                show it.
              </>
            ) : (
              <>
                &ldquo;{outcome?.summary.title}&rdquo; has been created. The
                discography is refreshing to show it.
              </>
            )}
          </p>
          <div className="tw-flex tw-justify-end tw-gap-2">
            <WButton
              variant="secondary"
              label="Create another"
              onClick={createAnother}
            />
            {/* Straight out, not through the guard: the draft was submitted,
                so there is nothing left to discard. */}
            <WButton
              variant="primary"
              label="Done"
              onClick={() => {
                setOutcome(null);
                onClose();
              }}
            />
          </div>
        </div>
      </WModal>

      <WModal
        isOpen={confirmDiscard}
        onClose={() => setConfirmDiscard(false)}
        title="Discard this recording?"
        modalClasses="tw-max-w-md tw-w-full tw-mx-4"
      >
        <div className="tw-mt-4">
          <p className="tw-text-white tw-text-sm tw-mb-6">
            This recording has not been {adding ? 'added' : 'created'} yet.
            Closing now discards everything you have entered.
          </p>
          <div className="tw-flex tw-justify-end tw-gap-2">
            {/* Keep editing is the safe default and reads first, so the
                destructive option is never the one under the cursor. */}
            <WButton
              variant="secondary"
              label="Keep editing"
              onClick={() => setConfirmDiscard(false)}
            />
            <WButton variant="primary" label="Discard" onClick={discard} />
          </div>
        </div>
      </WModal>
    </WModal>
  );
};

export default CreateRecordingModal;
